import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  eventType: string;
  clientId: string | null;
  clientName: string | null;
  caseId: string | null;
  caseTitle: string | null;
  leadId: string | null;
  leadName: string | null;
  taskId: string | null;
  taskTitle: string | null;
  startAt: string;
  endAt: string | null;
  location: string | null;
  status: string;
  responsibleUser: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCalendarEventInput {
  title: string;
  description?: string | null;
  eventType?: string | null;
  clientId?: string | null;
  caseId?: string | null;
  leadId?: string | null;
  taskId?: string | null;
  startAt: string;
  endAt?: string | null;
  location?: string | null;
  status?: string;
  responsibleUser?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

export type UpdateCalendarEventInput = Partial<CreateCalendarEventInput>;

export type LoadCalendarEventsFilters = {
  includeArchived?: boolean;
  search?: string;
  status?: string;
  eventType?: string;
  clientId?: string;
  caseId?: string;
  leadId?: string;
  responsibleUser?: string;
  startDate?: string;
  endDate?: string;
};

type UseCalendarEventsOptions = {
  autoLoad?: boolean;
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toBoolean = (value: unknown, defaultValue = true) => {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  const normalized = String(value).trim().toLowerCase();

  if (['0', 'false', 'inactive', 'inativo', 'archived', 'arquivado', 'nao', 'não'].includes(normalized)) {
    return false;
  }

  if (['1', 'true', 'active', 'ativo', 'sim', 's', 'on'].includes(normalized)) {
    return true;
  }

  return Boolean(value);
};

const statusPriority: Record<string, number> = {
  scheduled: 0,
  completed: 1,
  cancelled: 2,
  archived: 3,
};

const toTimestamp = (value: string | null) => {
  if (!value) {
    return Number.POSITIVE_INFINITY;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const sortCalendarEvents = (a: CalendarEvent, b: CalendarEvent) => {
  const aPriority = statusPriority[a.status] ?? 99;
  const bPriority = statusPriority[b.status] ?? 99;

  if (aPriority !== bPriority) {
    return aPriority - bPriority;
  }

  const aStart = toTimestamp(a.startAt);
  const bStart = toTimestamp(b.startAt);
  if (aStart !== bStart) {
    return aStart - bStart;
  }

  const aCreated = toTimestamp(a.createdAt);
  const bCreated = toTimestamp(b.createdAt);
  if (aCreated !== bCreated) {
    return bCreated - aCreated;
  }

  return b.id.localeCompare(a.id);
};

const normalizeCalendarEvent = (value: any): CalendarEvent => ({
  id: String(value?.id ?? ''),
  title: String(value?.title ?? '').trim(),
  description: toNullableText(value?.description),
  eventType: String(value?.eventType ?? value?.event_type ?? 'outro').trim() || 'outro',
  clientId: toNullableText(value?.clientId ?? value?.client_id),
  clientName: toNullableText(value?.clientName ?? value?.client_name),
  caseId: toNullableText(value?.caseId ?? value?.case_id),
  caseTitle: toNullableText(value?.caseTitle ?? value?.case_title),
  leadId: toNullableText(value?.leadId ?? value?.lead_id),
  leadName: toNullableText(value?.leadName ?? value?.lead_name),
  taskId: toNullableText(value?.taskId ?? value?.task_id),
  taskTitle: toNullableText(value?.taskTitle ?? value?.task_title),
  startAt: toNullableText(value?.startAt ?? value?.start_at) ?? new Date().toISOString(),
  endAt: toNullableText(value?.endAt ?? value?.end_at),
  location: toNullableText(value?.location),
  status: String(value?.status ?? 'scheduled').trim() || 'scheduled',
  responsibleUser: toNullableText(value?.responsibleUser ?? value?.responsible_user),
  notes: toNullableText(value?.notes),
  isActive: toBoolean(value?.isActive ?? value?.is_active, true),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

const sortAndSetCalendarEvents = (
  setCalendarEvents: Dispatch<SetStateAction<CalendarEvent[]>>,
  next: CalendarEvent[]
) => {
  setCalendarEvents([...next].sort(sortCalendarEvents));
};

export function useCalendarEvents(options: UseCalendarEventsOptions = {}) {
  const { autoLoad = true } = options;
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const lastFiltersRef = useRef<LoadCalendarEventsFilters>({});

  const loadCalendarEvents = useCallback(async (filters: LoadCalendarEventsFilters = {}) => {
    const requestId = ++requestIdRef.current;
    lastFiltersRef.current = filters;
    setLoading(true);
    setError(null);

    try {
      const searchParams = new URLSearchParams();

      if (filters.includeArchived) {
        searchParams.set('includeArchived', '1');
      }

      if (filters.search?.trim()) {
        searchParams.set('search', filters.search.trim());
      }

      if (filters.status?.trim()) {
        searchParams.set('status', filters.status.trim());
      }

      if (filters.eventType?.trim()) {
        searchParams.set('eventType', filters.eventType.trim());
      }

      if (filters.clientId?.trim()) {
        searchParams.set('clientId', filters.clientId.trim());
      }

      if (filters.caseId?.trim()) {
        searchParams.set('caseId', filters.caseId.trim());
      }

      if (filters.leadId?.trim()) {
        searchParams.set('leadId', filters.leadId.trim());
      }

      if (filters.responsibleUser?.trim()) {
        searchParams.set('responsibleUser', filters.responsibleUser.trim());
      }

      if (filters.startDate?.trim()) {
        searchParams.set('startDate', filters.startDate.trim());
      }

      if (filters.endDate?.trim()) {
        searchParams.set('endDate', filters.endDate.trim());
      }

      const endpoint = searchParams.toString()
        ? `/api/calendar-events?${searchParams.toString()}`
        : '/api/calendar-events';

      const response = await fetchWithRetry(endpoint);
      const data = await response.json();

      if (!response.ok || !data.success || !Array.isArray(data.calendarEvents)) {
        throw new Error(data.error || `GET /api/calendar-events falhou com status ${response.status}`);
      }

      if (requestId === requestIdRef.current) {
        sortAndSetCalendarEvents(setCalendarEvents, data.calendarEvents.map(normalizeCalendarEvent));
      }
    } catch (loadError) {
      console.error('Erro ao carregar agenda interna real.', loadError);

      if (requestId === requestIdRef.current) {
        setError('Não foi possível carregar a agenda interna real.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const refetch = useCallback(() => loadCalendarEvents(lastFiltersRef.current), [loadCalendarEvents]);

  const createCalendarEvent = useCallback(async (input: CreateCalendarEventInput) => {
    const response = await fetchWithRetry('/api/calendar-events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.calendarEvent) {
      throw new Error(data.error || `POST /api/calendar-events falhou com status ${response.status}`);
    }

    const created = normalizeCalendarEvent(data.calendarEvent);
    setCalendarEvents((prev) => [created, ...prev.filter((event) => event.id !== created.id)].sort(sortCalendarEvents));
    return created;
  }, []);

  const updateCalendarEvent = useCallback(async (id: string, input: UpdateCalendarEventInput) => {
    const response = await fetch(`/api/calendar-events/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.calendarEvent) {
      throw new Error(data.error || `PUT /api/calendar-events/${id} falhou com status ${response.status}`);
    }

    const updated = normalizeCalendarEvent(data.calendarEvent);
    setCalendarEvents((prev) => [updated, ...prev.filter((event) => event.id !== updated.id)].sort(sortCalendarEvents));
    return updated;
  }, []);

  const archiveCalendarEvent = useCallback(async (id: string) => {
    const response = await fetch(`/api/calendar-events/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `DELETE /api/calendar-events/${id} falhou com status ${response.status}`);
    }

    const archivedAt = new Date().toISOString();
    setCalendarEvents((prev) =>
      prev
        .map((event) =>
          event.id === id
            ? {
                ...event,
                status: 'archived',
                isActive: false,
                updatedAt: archivedAt,
              }
            : event
        )
        .sort(sortCalendarEvents)
    );

    return true;
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void loadCalendarEvents();
  }, [autoLoad, loadCalendarEvents]);

  return {
    calendarEvents,
    loading,
    error,
    refetch,
    loadCalendarEvents,
    createCalendarEvent,
    updateCalendarEvent,
    archiveCalendarEvent,
  };
}
